// Messages/useMessagesData.js
// Custom hook for managing messages data and user information

import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy, getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';

export const useMessagesData = () => {
  const [threads, setThreads] = useState([]);
  const [userInfos, setUserInfos] = useState({}); // { uid: { name, profilePic } }
  const [loading, setLoading] = useState(true);
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', user.uid),
      orderBy('lastUpdated', 'desc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const threadsData = snapshot.docs.map((doc) => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      
      setThreads(threadsData);

      // Fetch other user info for each thread
      const otherUids = threadsData
        .map(t => (t.participants || []).find(uid => uid !== user.uid))
        .filter(Boolean);
      
      const uniqueUids = [...new Set(otherUids)];

      // Only fetch user info we don't already have
      const uidsToFetch = uniqueUids.filter(uid => !userInfos[uid]);
      
      if (uidsToFetch.length > 0) {
        const newInfos = {};
        await Promise.all(uidsToFetch.map(async (uid) => {
          try {
            const userDoc = doc(db, 'users', uid);
            const snap = await getDoc(userDoc);
            if (snap.exists()) {
              newInfos[uid] = snap.data();
            }
          } catch (error) {
            newInfos[uid] = { name: 'Unknown User', profilePic: '' };
          }
        }));

        // Update state with new user infos
        setUserInfos(prev => ({ ...prev, ...newInfos }));
      }

      setLoading(false);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // REMOVED userInfos from dependencies to prevent infinite loop

  return {
    threads,
    userInfos,
    loading,
    currentUser: user
  };
};